import React, { useEffect } from "react";
import { CircularProgress, Typography, Box } from "@mui/material";
import AuthenticationService from "../AuthenticationService";
import { setActivePage, setAuthentication, setLayout } from "context";
import { useMaterialUIController } from "context";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { useParams } from "react-router-dom";
import { loginWithKeyList } from "globals/configuration/loginWithKeyList";

const LoginWithKey = () => {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage } = controller;
  const navigate = useNavigate();
  const { key } = useParams();

  const isRole = ["ROLE_ACD_SUPERVISOR"];

  const hasAllowedRole = (userRoles = []) => {
    return userRoles.some((role) => isRole.includes(role));
  };

  const encryptData = (data, secretKey) => {
    const iv = CryptoJS.lib.WordArray.random(16);
    const ciphertext = CryptoJS.AES.encrypt(
      data,
      CryptoJS.enc.Utf8.parse(secretKey),
      {
        iv: iv,
        mode: CryptoJS.mode.CFB,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    return iv.concat(ciphertext.ciphertext).toString(CryptoJS.enc.Base64);
  };

  useEffect(() => {
    const handleLoginResponse = (data, key) => {
      if (!data.token || !data.refreshToken) {
        console.error("Invalid login response:", data);
        return;
      }

      AuthenticationService.storeAuthenticationDetails(
        data.sipPhoneButton,
        data.token,
        data.refreshToken,
        data.userName,
        data.fullName,
        data.roles,
        data.menu,
        data.coralApplication,
        {
          extensionName: data.extensionName,
          extension: data.extension,
          sipPassword: data.sipPassword,
        },
        data.rluCode,
        data.rluIp,
        data.pwdComplication,
        data.userId,
        data.ringAnswerCount,
        data.publicEmergencyNumber,
        data.broadcastNumber,
        data.wssDomain,
        data.mapUrl,
        data.mapCenter,
        data.mapDefaultZoom,
        data.mapMinZoom,
        data.mapMaxZoom,
        data.xmPPPrimaryDomain,
        data.departmentList,
        data.idealTimeout,
        data.description,
        data.appMode,
        data.enableTTS,
        data.disableContextBasedGui,
        data.turnServerUrl,
        data.turnUserName,
        data.turnPassword,
        {
          currencySymbol:      data?.currencySymbol,
          decimalSeparator:    data?.decimalSeparator,
          groupingSeparator:   data?.groupingSeparator,
          groupingStyle:       data?.groupingStyle,
          taxLabel:            data?.taxLabel,
          taxRounding:         data?.taxRounding,
          roundingMode:        data?.roundingMode,
          amountInWords:       data?.amountInWords,
          currencyWord:        data?.currencyWord,
          currencyFractionWord: data?.currencyFractionWord,
        },
      );

      window.sessionStorage.setItem(
        `${window.location.hostname}-coral-X-User`,
        encryptData(data.username, window.secretKey),
      );

      window.sessionStorage.setItem(
        `${window.location.hostname}-coral-X-Page-Type`,
        key,
      );

      const userRoles = data?.roles || [];

      const matchedPage = loginWithKeyList.find((page) => page.key === key);

      if (matchedPage) {
        if (key === "active.conference.participants") {
          setAuthentication(dispatch, true);
          setLayout(dispatch, "dashboard");
          navigate("/ActiveConference", { replace: true });
          return;
        }
        activePage.key = matchedPage.key;
        activePage.name = matchedPage.name;
        activePage.description = matchedPage.description;
        activePage.url = matchedPage.url;
        activePage.authenticationStatus = true;
        setActivePage(dispatch, activePage);
        setAuthentication(dispatch, true);
        setLayout(dispatch, "dashboard");

        if (hasAllowedRole(userRoles)) {
          window.location.href = "/home-page";
          return;
        }
        navigate("/app/home", { replace: true });
      }
    };

    const handleMessage = (event) => {
      if (event?.data?.eventType === "loginResponse") {
        console.log("Login response received:", event.data);
        handleLoginResponse(event.data.data, key);
      } else {
        console.error("Unexpected event data:", event.data);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [activePage, dispatch, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress size={60} thickness={4} color="info" />
      <Typography
        variant="h6"
        sx={{ marginTop: 2, color: "text", fontSize: "25px" }}
      >
        Please wait, Loading...
      </Typography>
    </Box>
  );
};

export default LoginWithKey;
